import { useQuery } from '@tanstack/react-query';
import { errorMessage, request } from '../api';

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  actor: { username: string; role: string } | null;
  metadata: unknown;
  createdAt: string;
}

/**
 * The audit trail, surfaced.
 *
 * Answers "who removed that bidder, and when" — questions that only get asked
 * after something has gone wrong, which is exactly when the answer needs to
 * already exist rather than be reconstructible.
 */
export function Activity(): JSX.Element {
  const { data, error, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => request<AuditEntry[]>('GET', '/audit?limit=200'),
  });

  if (isLoading) return <p className="muted">Loading activity…</p>;
  if (error) return <p className="error">{errorMessage(error)}</p>;

  return (
    <>
      <header className="page-head">
        <h1>Activity</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Bids placed, bidders assigned and removed, invitations issued and revoked.
        </p>
      </header>

      {data?.length === 0 && <div className="card muted">Nothing recorded yet.</div>}

      {data && data.length > 0 && (
        <section className="card">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>By</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <tr key={entry.id}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(entry.createdAt).toLocaleString()}
                  </td>
                  <td>{describe(entry.action)}</td>
                  <td className="muted">
                    {/* Null for the retention job and other system actions —
                        attributing those to a person would be a lie. */}
                    {entry.actor ? entry.actor.username : <em>system</em>}
                  </td>
                  <td className="muted small truncate">{summarize(entry.metadata)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

const ACTIONS: Record<string, string> = {
  'bid.placed': 'Bid placed',
  'invitation.accepted': 'Invitation accepted',
};

function describe(action: string): string {
  return ACTIONS[action] ?? action;
}

function summarize(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object') return '';
  return Object.entries(metadata as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}
